import React, { useEffect, useState } from 'react'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Box, IconButton, Modal } from '@mui/material'
import { TrashIcon } from '../../components/icons/TrashIcon'
import { CustomModal } from '../../components/modal/CustomModal'
import AddIcon from '@mui/icons-material/Add'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { forkJoin, of, throwError } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import styles from './Detail.module.css'
import { PageLayout } from 'pages/layout'
import { getUserContainer } from '../../container/user-module'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { emptyUserDTO, fromModel, User, UserDTO } from '../../modules/users/models/User'
import { emptyPatientFamily, PatientFamily } from '../../modules/users/models/PatientFamily'
import { Query } from '../../common/api/Query'
import { getFileContainer } from '../../container/file-module'
import { FileService } from '../../modules/files/services/FileService'
import { FILE_SERVICE_KEY } from '../../modules/files'
import { useUserRoles } from '../../modules/users/hooks/useUserRoles'

type DetailProps = {
  id?: string
}

type Relative = {
  id: string
  // Empty on drafts: the familiar user does not exist yet.
  familiarID: string
  firstName: string
  lastName: string
  relationship: string
  phone: string
  email: string
  isNew: boolean
}

const emptyRelative = (id: string): Relative => ({
  id,
  familiarID: '',
  firstName: '',
  lastName: '',
  relationship: '',
  phone: '',
  email: '',
  isNew: true,
})

const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const fileService = getFileContainer().get<FileService>(FILE_SERVICE_KEY)

export function Detail(props: DetailProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [user, setUser] = useState<User | null>(null)
  const [relatives, setRelatives] = useState<Relative[]>([])
  const [photo, setPhoto] = useState<string>('')
  // Untouched server copy, to diff changes and to restore on Cancel without refetching.
  const [savedRelatives, setSavedRelatives] = useState<Relative[]>([])
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [refresh, setRefresh] = useState<number>(0)
  const [relativeToRemove, setRelativeToRemove] = useState<Relative | null>(null)

  const [isEditingData, setIsEditingData] = useState<boolean>(false)
  const [userData, setUserData] = useState<UserDTO>(emptyUserDTO())

  const { canEditPatientData, canManagePatientCircle, isFamiliar } = useUserRoles(
    loggedUserService.get()
  )
  const canEditRelatives = canManagePatientCircle && !isFamiliar

  useEffect(() => {
    if (!props.id) return
    userService.getByID(props.id).subscribe((u) => {
      if (!u) return
      setUser(u)
      setUserData(fromModel(u))
    })
    fileService.getProfilePhotoByUserID(props.id).subscribe((f) => setPhoto(f?.data ?? ''))
  }, [props.id])

  // A family relation stores only relationship and phone.
  // Name and email have to come from the familiar user.
  useEffect(() => {
    if (!props.id) return
    userService.getFamilies(props.id).subscribe((families: PatientFamily[]) => {
      if (!families.length) {
        setRelatives([])
        return
      }
      forkJoin(families.map((f) => userService.getByID(f.familiarID))).subscribe((users) => {
        const loaded: Relative[] = families.map((f, i) => {
          const u = users[i]
          return {
            id: f.id,
            familiarID: f.familiarID,
            firstName: u?.firstName ?? '',
            lastName: u?.lastName ?? '',
            relationship: f.relationship,
            phone: f.phone,
            email: u?.email ?? '',
            isNew: false,
          }
        })
        setRelatives(loaded)
        setSavedRelatives(loaded)
      })
    })
  }, [props.id, refresh])

  const removeRelative = () => {
    const r = relativeToRemove
    if (!r || !user) return
    setRelativeToRemove(null)

    if (r.isNew) {
      setRelatives((prev) => prev.filter((x) => x.id !== r.id))
      return
    }

    userService.removeFamilyRelation(user.id, r.familiarID).subscribe(() => {
      enqueueSnackbar(t('changesSaved'), { variant: 'success' })
      setRefresh((p) => p + 1)
    })
  }

  const resetPassword = () => {
    if (!user?.email) return
    loggedUserService.sendRecoverPassword(user.email).subscribe(() => {
      enqueueSnackbar(t('recoverPasswordEmailSent'), { variant: 'success' })
    })
  }

  const patch = (id: string, change: Partial<Relative>) =>
    setRelatives((prev) => prev.map((r) => (r.id === id ? { ...r, ...change } : r)))

  const addDraft = () =>
    setRelatives((prev) => [...prev, emptyRelative(`draft-${Date.now()}-${prev.length}`)])

  const cancelChanges = () => setRelatives(savedRelatives)

  const isDirty =
    relatives.some((r) => r.isNew) ||
    savedRelatives.some((orig) => {
      const current = relatives.find((r) => r.id === orig.id)
      return (
        !current ||
        current.relationship !== orig.relationship ||
        current.phone !== orig.phone ||
        current.email !== orig.email
      )
    })

  // Typing a draft's email links an existing familiar instead of creating a
  // duplicate user, and fills in the name.
  const lookupByEmail = (id: string, email: string) => {
    patch(id, { email, familiarID: '' })
    if (!email.includes('@')) return

    userService
      .getFilteredList(
        new Query({ query: [{ name: 'email', value: email }], pager: { offset: 0, limit: 1 } })
      )
      .subscribe((res) => {
        const found = res.items[0]
        if (!found) return
        patch(id, {
          familiarID: found.id,
          firstName: found.firstName,
          lastName: found.lastName,
        })
      })
  }

  const saveRelatives = () => {
    if (isSaving) return

    const drafts = relatives.filter((r) => r.isNew)
    const missingData = drafts.some(
      (r) => !r.firstName || !r.lastName || !r.email || !r.relationship
    )
    if (missingData) {
      enqueueSnackbar(t('requiredField'), { variant: 'error' })
      return
    }

    setIsSaving(true)

    // Relationship and phone live on the relation; the email belongs to the
    // familiar user, so it is updated separately.
    const modified = relatives.filter((r) => {
      if (r.isNew) return false
      const orig = savedRelatives.find((o) => o.id === r.id)
      return (
        orig &&
        (orig.relationship !== r.relationship ||
          orig.phone !== r.phone ||
          orig.email !== r.email ||
          orig.firstName !== r.firstName ||
          orig.lastName !== r.lastName)
      )
    })

    const requests = [
      ...modified.map((r) => {
        const orig = savedRelatives.find((o) => o.id === r.id)
        const relation = userService.updateFamilyRelation(user!.id, r.familiarID, {
          ...emptyPatientFamily(user!.id),
          familiarID: r.familiarID,
          relationship: r.relationship,
          phone: r.phone,
        })
        const sameUser =
          orig &&
          orig.email === r.email &&
          orig.firstName === r.firstName &&
          orig.lastName === r.lastName
        if (sameUser) return relation

        return relation.pipe(
          switchMap(() =>
            userService.getByID(r.familiarID).pipe(
              switchMap((u) => {
                if (!u) return of(undefined)
                const dto = fromModel(u)
                dto.email = r.email
                dto.firstName = r.firstName
                dto.lastName = r.lastName
                return userService.update(dto)
              })
            )
          )
        )
      }),
      ...drafts.map((r) => {
        const relation = (familiarID: string) =>
          userService.addFamilyRelation({
            ...emptyPatientFamily(user!.id),
            familiarID,
            relationship: r.relationship,
            phone: r.phone,
          })

        if (r.familiarID) return relation(r.familiarID)

        const newFamiliar: UserDTO = {
          ...emptyUserDTO(),
          firstName: r.firstName,
          lastName: r.lastName,
          email: r.email,
          isPatient: false,
          language: userData.language,
        }
        return userService.addFamiliar(newFamiliar, navigator.language).pipe(
          switchMap((created) => {
            // No user means no relation to create: fail instead of reporting a
            // save that did not happen.
            if (!created) return throwError(() => new Error('familiar not created'))
            return relation(created.id)
          })
        )
      }),
    ]

    if (!requests.length) {
      setIsSaving(false)
      return
    }

    forkJoin(requests).subscribe({
      next: () => {
        setIsSaving(false)
        enqueueSnackbar(t('changesSaved'), { variant: 'success' })
        setRefresh((p) => p + 1)
      },
      error: () => {
        setIsSaving(false)
        enqueueSnackbar(t('anErrorOccured'), { variant: 'error' })
      },
    })
  }

  const editable = (label: string, v: string, ph: string, onChange: (s: string) => void) => (
    <Box className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <input
        className={styles.inlineInput}
        value={v}
        placeholder={ph}
        onChange={(e) => onChange(e.target.value)}
      />
    </Box>
  )

  const saveUserData = () => {
    if (isSaving || !props.id) return
    setIsSaving(true)
    userService.update(userData).subscribe({
      next: () => {
        setIsSaving(false)
        setIsEditingData(false)
        enqueueSnackbar(t('changesSaved'), { variant: 'success' })
        setRefresh((p) => p + 1)
      },
      error: () => {
        setIsSaving(false)
        enqueueSnackbar(t('anErrorOccured'), { variant: 'error' })
      },
    })
  }

  const cancelUserEdit = () => {
    if (user) setUserData(fromModel(user))
    setIsEditingData(false)
  }

  const userField = (
    label: string,
    value: string | undefined,
    onChange?: (s: string) => void,
    leftOnly = false
  ) =>
    isEditingData && onChange
      ? editable(label, value ?? '', label, onChange)
      : field(label, value, leftOnly)

  const relativeField = (
    label: string,
    value: string,
    ph?: string,
    onChange?: (s: string) => void
  ) => (
    <Box className={styles.stackedField}>
      <span className={styles.stackedLabel}>{label}</span>
      {onChange ? (
        <input
          className={styles.inlineInput}
          value={value}
          placeholder={ph}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <span className={`${styles.fieldValue} ${value ? '' : styles.empty}`}>{value || '—'}</span>
      )}
    </Box>
  )

  const field = (label: string, v?: string, leftOnly = false) => (
    <Box className={`${styles.field} ${leftOnly ? styles.leftOnly : ''}`}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={`${styles.fieldValue} ${v ? '' : styles.empty}`}>{v || '—'}</span>
    </Box>
  )

  if (!user) {
    return <PageLayout>{null}</PageLayout>
  }

  const location = [user.city, user.country].filter(Boolean).join(', ')
  const conditions = user.patologies?.map((p) => t(p)).join(', ') ?? ''
  const role = user.roles?.length ? t(user.roles[0]) : t('user')

  return (
    <PageLayout>
      <Box className={styles.container}>
        <Box className={styles.headerRow}>
          <Box>
            <p className={styles.name}>{`${user.firstName} ${user.lastName}`.trim()}</p>
            <p className={styles.role}>{role}</p>
          </Box>
          {user.sensitive && <span className={styles.sensitiveTag}>{t('sensitive')}</span>}
        </Box>

        <p className={styles.sectionTitle}>{t('personalData')}</p>
        <Box className={styles.card}>
          <Box className={styles.personalData}>
            <Box className={styles.avatarWrapper}>
              {photo ? (
                <img
                  className={styles.avatar}
                  src={`data:image/jpg;base64, ${photo}`}
                  alt={user.firstName}
                />
              ) : (
                <div className={styles.avatar} />
              )}
            </Box>
            <Box className={styles.fields}>
              {userField(t('name'), userData.firstName, (v) =>
                setUserData({ ...userData, firstName: v })
              )}
              {userField(t('surname'), userData.lastName, (v) =>
                setUserData({ ...userData, lastName: v })
              )}
              {userField(t('email'), userData.email, (v) => setUserData({ ...userData, email: v }))}
              {userField(t('telephoneNumber'), userData.phone, (v) =>
                setUserData({ ...userData, phone: v })
              )}
              {isEditingData ? (
                <>
                  {editable(t('city'), userData.city ?? '', t('city'), (v) =>
                    setUserData({ ...userData, city: v })
                  )}
                  {editable(t('country'), userData.country ?? '', t('country'), (v) =>
                    setUserData({ ...userData, country: v })
                  )}
                </>
              ) : (
                field(t('cityCountry'), location, true)
              )}
              {/* Conditions come from a catalogue, so they stay read-only here
                  and are managed in their own form. */}
              {field(t('medicalConditions'), conditions, true)}
            </Box>
          </Box>
        </Box>

        <Box className={styles.sectionHeader}>
          <p className={styles.sectionTitle} style={{ margin: 0 }}>
            {t('relatives')}
          </p>
          {canEditRelatives && (
            <button type="button" className={styles.pill} onClick={addDraft}>
              <AddIcon className={styles.pillIcon} />
              {t('addRelative')}
            </button>
          )}
        </Box>
        <Box className={styles.card}>
          {relatives.length === 0 ? (
            <span className={`${styles.fieldValue} ${styles.empty}`}>{t('noRelatives')}</span>
          ) : (
            relatives.map((r) => (
              <Box key={r.id} className={styles.relative}>
                <Box className={styles.relativeGrid}>
                  {canEditRelatives ? (
                    <>
                      {relativeField(t('name'), r.firstName, t('name'), (v) =>
                        patch(r.id, { firstName: v })
                      )}
                      {relativeField(t('surname'), r.lastName, t('surname'), (v) =>
                        patch(r.id, { lastName: v })
                      )}
                      <Box className={styles.relativeRemove}>
                        <IconButton
                          size="small"
                          title={t('removeRelative')}
                          onClick={() => setRelativeToRemove(r)}
                        >
                          <TrashIcon />
                        </IconButton>
                      </Box>
                      {relativeField(t('relationship'), r.relationship, t('relationship'), (v) =>
                        patch(r.id, { relationship: v })
                      )}
                      {relativeField(t('email'), r.email, 'email@example.com', (v) =>
                        r.isNew ? lookupByEmail(r.id, v) : patch(r.id, { email: v })
                      )}
                      {relativeField(t('telephoneNumber'), r.phone, '+34 600 000 000', (v) =>
                        patch(r.id, { phone: v })
                      )}
                    </>
                  ) : (
                    <>
                      {relativeField(t('name'), r.firstName)}
                      {relativeField(t('surname'), r.lastName)}
                      <span />
                      {relativeField(t('relationship'), r.relationship)}
                      {relativeField(t('email'), r.email)}
                      {relativeField(t('telephoneNumber'), r.phone)}
                    </>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>

        {canEditRelatives && (
          <Box className={styles.relativesFooter}>
            <Box className={styles.footerActions}>
              <button
                type="button"
                className={styles.saveChanges}
                onClick={saveRelatives}
                disabled={!isDirty || isSaving}
              >
                <SaveOutlinedIcon className={styles.pillIcon} />
                {t('saveChanges')}
              </button>
              <button
                type="button"
                className={styles.pillOutlined}
                onClick={cancelChanges}
                disabled={!isDirty}
              >
                {t('cancel')}
              </button>
            </Box>
          </Box>
        )}

        <Box className={styles.footer}>
          {canEditPatientData && !isFamiliar && !isEditingData && (
            <button type="button" className={styles.pill} onClick={() => setIsEditingData(true)}>
              <EditOutlinedIcon className={styles.pillIcon} />
              {t('editUser')}
            </button>
          )}
          {isEditingData && (
            <>
              <button
                type="button"
                className={styles.saveChanges}
                onClick={saveUserData}
                disabled={isSaving}
              >
                <SaveOutlinedIcon className={styles.pillIcon} />
                {t('saveChanges')}
              </button>
              <button type="button" className={styles.pillOutlined} onClick={cancelUserEdit}>
                {t('cancel')}
              </button>
            </>
          )}
          <button type="button" className={styles.pill} onClick={resetPassword}>
            {t('resetPassword')}
          </button>
        </Box>
      </Box>

      <Modal open={Boolean(relativeToRemove)} onClose={() => setRelativeToRemove(null)}>
        <CustomModal
          title={t('removeRelative')}
          warningText={t('irreversibleRemoveRelativeAction')}
          destructive
          confirmLabel={t('remove')}
          handleClose={() => setRelativeToRemove(null)}
          handleSave={removeRelative}
        />
      </Modal>
    </PageLayout>
  )
}
